import {AhpTimelineView} from '@github-ui/agent-sessions/components/session-logs/ahp/AhpTimelineView'
import {AhpSavedTimeline} from '@github-ui/agent-sessions/components/session-logs/ahp/AhpSessionHistoryTimeline'
import type {TaskAhpHistory} from '@github-ui/agent-sessions/hooks/use-task-ahp-history'
import type {TimelineMarker} from '@github-ui/agent-sessions/components/session-logs/ahp/AhpTimelineView'
import {SessionLogsProvider} from '@github-ui/agent-sessions/components/session-logs/Context'
import {SessionMetadataRow} from '@github-ui/agent-sessions/components/SessionMetadataRow'
import type {TaskStartup} from '@github-ui/agent-sessions/ahp/task-startup-store'
import {SessionContextProvider} from '@github-ui/agent-sessions/contexts/SessionContext'
import {SessionState, type Session} from '@github-ui/agent-sessions/types/session'
import {reconcilePersistedTimelineState, resolveTimelineView} from '@github-ui/ahp-relay/ahp-session-selectors'
import {
  createEstablishedAhpPendingMessage,
  type ContributedInstructionsMetadata,
  type EstablishedAhpSession,
  type EstablishedAhpSessionViewSnapshot,
} from '@github-ui/ahp-session-client/contracts'
import {getCurrentUserLogin} from '@github-ui/client-env'
import {HighlightedUserText} from '@github-ui/copilot-code-prettify/render/HighlightedUserText'
import {useAutoScroll} from '@github-ui/copilot-shared-ui/hooks/use-auto-scroll'
import {useAgentModels} from '@github-ui/copilot-shared-ui/agents/use-agent-models'
import {useOptionalEstablishedAhpSession} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session'
import {useOptionalEstablishedAhpSessionViewState} from '@github-ui/copilot-shared-ui/hooks/use-established-ahp-session-view'
import {useResizeObserver} from '@github-ui/use-resize-observer'
import type {ChatState, PendingMessage} from '@microsoft/agent-host-protocol'
import {ArrowDownIcon} from '@primer/octicons-react'
import {IconButton, useMergedRefs} from '@primer/react'
import {useCallback, useImperativeHandle, useLayoutEffect, useMemo, useRef} from 'react'
import type {ReactNode, Ref} from 'react'

import {
  useNativeAhpSession,
  useNativeAhpTimelineFallbackState,
  useNativeAhpTimelineState,
} from '../providers/use-timeline-contexts'
import styles from '../ChatApp.module.css'
import type {AhpSessionIdentifiers, ChatDisplayIdentity} from '../types'
import {ModelChangeMarkerRow} from './ModelChangeMarkerRow'
import {projectModelChangeMarkers} from '../providers/ModelChangeContext'

const CHAT_SESSION_CREATED_AT = '2026-01-01T00:00:00.000Z'
const EMPTY_CHAT_STATE = {status: 1, turns: []} as unknown as ChatState
const TIMELINE_BOTTOM_THRESHOLD = 64

export type TimelineAutoScrollHandle = {
  scrollToBottom: (behavior?: ScrollBehavior | 'instant') => void
  preserveOffsetForNextContentPrepend: () => void
}

// Module-level and stable so the SessionLogsProvider's memoized context value doesn't
// change identity on every render. Returning null for non-fenced text keeps the
// default plain-text bubble path byte-for-byte unchanged for ordinary messages.
function renderUserMessageContent(text: string, {collapsible}: {collapsible: boolean}) {
  return text.includes('```') ? <HighlightedUserText text={text} collapsible={collapsible} /> : null
}

function hasLocalTimelineState(state: ChatState | undefined): state is ChatState {
  return !!state && ((state.turns?.length ?? 0) > 0 || !!state.activeTurn)
}

function createChatSession(
  {taskId, environmentId, sessionId}: ChatDisplayIdentity,
  userLogin: string | undefined,
  displaySession?: ChatDisplayIdentity,
): Session {
  return {
    id: sessionId ?? 'quick-chat-memory',
    name: 'Chat',
    user_id: displaySession?.userId ?? 0,
    user_login: displaySession?.userLogin ?? userLogin,
    agent_id: displaySession?.agentId ?? 0,
    agent_slug: displaySession?.agentSlug ?? 'dotcom-chat',
    state: SessionState.InProgress,
    owner_id: 0,
    repo_id: 0,
    task_id: taskId,
    resource_type: 'quick_chat',
    resource_id: 0,
    resource_global_id: taskId,
    created_at: displaySession?.createdAt ?? CHAT_SESSION_CREATED_AT,
    last_updated_at: CHAT_SESSION_CREATED_AT,
    updated_at: CHAT_SESSION_CREATED_AT,
    workflow_run_id: 0,
    log_entries: [],
    error: null,
    premium_requests: 0,
    event_identifiers: displaySession?.eventIdentifiers ?? null,
    event_type: displaySession?.eventType ?? null,
    event_url: displaySession?.eventUrl ?? null,
    event_content: displaySession?.eventContent ?? null,
    model: '',
    environment_id: environmentId,
  }
}

function NativeAhpTimeline({
  ahpSession,
  displaySession,
  savedHistory,
  savedHistoryError,
  state,
  errorMessage,
  autoScrollRef,
  afterContent,
  suppressProgressMessage,
  establishedAhpSession,
  establishedAhpSessionView,
  establishedAhpSessionPending,
  establishedAhpSessionError,
  taskStartup,
  taskStartupCloneProgress,
  savedHistoryEnabled,
  loadingPresentation,
  queueReadOnly,
  onContributedInstructions,
}: {
  ahpSession?: AhpSessionIdentifiers
  displaySession?: ChatDisplayIdentity
  savedHistory?: TaskAhpHistory
  savedHistoryError?: string
  state: ReturnType<typeof useNativeAhpTimelineState>['state']
  errorMessage?: string
  autoScrollRef?: Ref<TimelineAutoScrollHandle>
  afterContent?: ReactNode
  suppressProgressMessage?: boolean
  establishedAhpSession?: EstablishedAhpSession
  establishedAhpSessionView?: EstablishedAhpSessionViewSnapshot
  establishedAhpSessionPending?: boolean
  establishedAhpSessionError?: string
  taskStartup?: TaskStartup
  taskStartupCloneProgress?: number
  savedHistoryEnabled?: boolean
  loadingPresentation?: 'local' | 'page'
  queueReadOnly?: boolean
  onContributedInstructions?: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
}) {
  const {models} = useAgentModels()
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const prependScrollSnapshotRef = useRef<{anchor: HTMLElement; anchorTop: number; scrollTop: number} | undefined>(
    undefined,
  )
  const currentUserLogin = getCurrentUserLogin()
  const runtimeAhpSession = useNativeAhpSession()
  const establishedIdentity = establishedAhpSession?.identity
  const establishedIdentityRef =
    establishedIdentity?.taskId && establishedIdentity.environmentId && establishedIdentity.sessionId
      ? {
          taskId: establishedIdentity.taskId,
          environmentId: establishedIdentity.environmentId,
          sessionId: establishedIdentity.sessionId,
          ...(establishedIdentity.channel ? {channel: establishedIdentity.channel} : {}),
        }
      : undefined
  const activeAhpSession = establishedIdentityRef ?? ahpSession ?? runtimeAhpSession
  const selectedSessionId = activeAhpSession?.sessionId ?? displaySession?.sessionId
  const selectedSavedSessionIndex = savedHistory?.findIndex(saved => saved.sessionId === selectedSessionId) ?? -1
  const savedHistoryBeforeSelected =
    selectedSavedSessionIndex >= 0 ? savedHistory?.slice(0, selectedSavedSessionIndex) : savedHistory
  const savedHistoryAfterSelected =
    selectedSavedSessionIndex >= 0 ? savedHistory?.slice(selectedSavedSessionIndex + 1) : undefined
  const historyBundle = savedHistory?.find(saved => saved.sessionId === selectedSessionId)?.bundle
  const TimelineView = savedHistoryEnabled || savedHistory ? AhpSavedTimeline : AhpTimelineView
  const timelineMarkers = useMemo<TimelineMarker[]>(() => {
    return projectModelChangeMarkers(state).map(marker => ({
      id: marker.id,
      afterTurnId: marker.afterTurnId,
      node: <ModelChangeMarkerRow marker={marker} />,
    }))
  }, [state])
  const establishedModelFailureMessages = establishedAhpSessionView?.modelFailures
    ? Object.fromEntries(
        Object.entries(establishedAhpSessionView.modelFailures).map(([turnId, failure]) => [
          turnId,
          failure.model
            ? `${failure.model} could not run: ${failure.message}`
            : `The model could not run: ${failure.message}`,
        ]),
      )
    : undefined
  const {showScrollToBottom, scrollToBottom, containerCallbackRef, contentCallbackRef} = useAutoScroll({
    containerRef: scrollContainerRef,
    resetKey: activeAhpSession?.sessionId ?? displaySession?.sessionId,
    bottomThreshold: TIMELINE_BOTTOM_THRESHOLD,
  })
  const mergedContentRef = useMergedRefs(contentRef, contentCallbackRef)
  const preserveOffsetForNextContentPrepend = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const {clientHeight, scrollHeight, scrollTop} = container
    const bottomDistance = scrollHeight - scrollTop - clientHeight
    const anchor =
      bottomDistance > TIMELINE_BOTTOM_THRESHOLD
        ? contentRef.current?.querySelector<HTMLElement>('ol[aria-label="Chat timeline"] > li')
        : undefined
    prependScrollSnapshotRef.current = anchor
      ? {anchor, anchorTop: anchor.getBoundingClientRect().top, scrollTop}
      : undefined
  }, [])
  // Reconnect can prepend live-only status UI above the reused persisted transcript.
  // Correct the retained transcript's displacement before paint so growth below it is ignored.
  useLayoutEffect(() => {
    const snapshot = prependScrollSnapshotRef.current
    if (!snapshot) return
    prependScrollSnapshotRef.current = undefined

    const container = scrollContainerRef.current
    if (!container || !snapshot.anchor.isConnected) return
    const anchorDelta = snapshot.anchor.getBoundingClientRect().top - snapshot.anchorTop
    if (anchorDelta !== 0) container.scrollTo({top: snapshot.scrollTop + anchorDelta, behavior: 'instant'})
  }, [establishedAhpSession])
  const handleScrollToBottomClick = useCallback(() => {
    requestAnimationFrame(() => {
      scrollContainerRef.current?.focus({preventScroll: true})
      scrollToBottom('smooth')
    })
  }, [scrollToBottom])
  const session = useMemo(
    () =>
      createChatSession(
        activeAhpSession ??
          displaySession ?? {
            taskId: 'quick-chat-memory',
            environmentId: 'quick-chat-memory',
            sessionId: 'quick-chat-memory',
          },
        currentUserLogin,
        displaySession,
      ),
    [activeAhpSession, currentUserLogin, displaySession],
  )
  useImperativeHandle(
    autoScrollRef,
    () => ({
      scrollToBottom,
      preserveOffsetForNextContentPrepend,
    }),
    [preserveOffsetForNextContentPrepend, scrollToBottom],
  )

  const observeViewport = useResizeObserver(entry => {
    if (entry.target instanceof HTMLElement) {
      const nextViewportBlockSize = `${entry.contentRect.height}px`
      if (entry.target.style.getPropertyValue('--timeline-viewport-block-size') !== nextViewportBlockSize) {
        entry.target.style.setProperty('--timeline-viewport-block-size', nextViewportBlockSize)
      }
    }
  })
  const viewportRef = useCallback(
    (node: HTMLDivElement | null) => {
      const stopObserving = observeViewport(node)
      return () => stopObserving?.()
    },
    [observeViewport],
  )
  const timelineRef = useMergedRefs(scrollContainerRef, useMergedRefs(viewportRef, containerCallbackRef))
  const renderSavedTimeline = (saved: TaskAhpHistory[number]) => (
    <SessionContextProvider key={saved.sessionId} session={{...session, id: saved.sessionId}}>
      <AhpSavedTimeline
        bundle={saved.bundle}
        state={undefined}
        view="ready"
        ariaLabel={`Saved session ${saved.sessionId}`}
      />
    </SessionContextProvider>
  )

  return (
    <div className={styles.NativeTimelineFrame}>
      <div
        ref={timelineRef}
        className={styles.NativeTimeline}
        role="region"
        // Keyboard users need to focus the overflow region to scroll the timeline.
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        aria-label="Conversation timeline"
        data-testid="quick-chat-native-ahp-timeline"
      >
        <div
          ref={mergedContentRef}
          className={styles.NativeTimelineContent}
          data-testid="quick-chat-native-ahp-timeline-content"
        >
          <SessionContextProvider session={session}>
            {session.event_type === 'legacy_chat' && <SessionMetadataRow session={session} environment="cloud" />}
            <SessionLogsProvider variant="chat" session={session} renderUserMessageContent={renderUserMessageContent}>
              {savedHistoryBeforeSelected?.map(renderSavedTimeline)}
              {establishedAhpSession ? (
                <TimelineView
                  bundle={historyBundle}
                  state={state}
                  view={resolveTimelineView(
                    !!state,
                    establishedAhpSessionView?.status ?? 'idle',
                    establishedAhpSessionView?.error,
                  )}
                  errorMessage={establishedAhpSessionView?.error}
                  ariaLabel="Chat timeline"
                  additionalErrorMessage={savedHistoryError ?? errorMessage}
                  timelineMarkers={timelineMarkers}
                  availableModels={models}
                  taskStartup={taskStartup}
                  taskStartupCloneProgress={taskStartupCloneProgress}
                  provisioning={establishedAhpSessionView?.provisioning}
                  loadingPresentation={loadingPresentation}
                  steeringMessages={establishedAhpSessionView?.steeringMessages}
                  {...(!queueReadOnly && establishedAhpSessionView?.passiveKnown && !establishedAhpSessionView.passive
                    ? {
                        onCancelQueuedMessage: (id: string) => establishedAhpSession.input.removeQueued(id),
                        onSendNowQueuedMessage: async (message: PendingMessage) => {
                          await establishedAhpSession.input.sendNow(createEstablishedAhpPendingMessage(message))
                        },
                      }
                    : {})}
                  {...(onContributedInstructions ? {onContributedInstructions} : {})}
                  {...(establishedModelFailureMessages ? {modelFailureMessages: establishedModelFailureMessages} : {})}
                  {...(!taskStartup && establishedAhpSessionView?.provisioning?.phase === 'cloning'
                    ? {provisioningMessage: 'Cloning repository…'}
                    : {})}
                />
              ) : establishedAhpSessionPending ? (
                <TimelineView
                  bundle={historyBundle}
                  state={taskStartup ? undefined : savedHistory ? state : (state ?? EMPTY_CHAT_STATE)}
                  view={establishedAhpSessionError ? 'error' : taskStartup ? 'loading' : 'ready'}
                  errorMessage={establishedAhpSessionError}
                  additionalErrorMessage={savedHistoryError}
                  suppressQueuedMessages
                  suppressProgressMessage={suppressProgressMessage}
                  taskStartup={taskStartup}
                  taskStartupCloneProgress={taskStartupCloneProgress}
                  ariaLabel="Chat timeline"
                  loadingPresentation={loadingPresentation}
                  {...(onContributedInstructions ? {onContributedInstructions} : {})}
                />
              ) : (
                <TimelineView
                  bundle={historyBundle}
                  state={taskStartup ? undefined : savedHistory ? state : (state ?? EMPTY_CHAT_STATE)}
                  view={!hasLocalTimelineState(state) && errorMessage ? 'error' : taskStartup ? 'loading' : 'ready'}
                  errorMessage={!hasLocalTimelineState(state) ? errorMessage : undefined}
                  additionalErrorMessage={
                    savedHistoryError ?? (hasLocalTimelineState(state) ? errorMessage : undefined)
                  }
                  timelineMarkers={timelineMarkers}
                  suppressQueuedMessages
                  suppressProgressMessage={suppressProgressMessage}
                  taskStartup={taskStartup}
                  taskStartupCloneProgress={taskStartupCloneProgress}
                  ariaLabel="Chat timeline"
                  availableModels={models}
                  loadingPresentation={loadingPresentation}
                  {...(onContributedInstructions ? {onContributedInstructions} : {})}
                />
              )}
              {savedHistoryAfterSelected?.map(renderSavedTimeline)}
            </SessionLogsProvider>
          </SessionContextProvider>
          {afterContent}
        </div>
      </div>
      <IconButton
        type="button"
        icon={ArrowDownIcon}
        aria-label="Scroll to bottom"
        className={styles.TimelineScrollToBottomButton}
        data-visible={showScrollToBottom ? 'true' : 'false'}
        tabIndex={showScrollToBottom ? undefined : -1}
        aria-hidden={!showScrollToBottom}
        onClick={handleScrollToBottomClick}
      />
    </div>
  )
}

export function Timeline({
  ahpSession,
  displaySession,
  savedHistory,
  savedHistoryError,
  stateOverride,
  autoScrollRef,
  afterContent,
  suppressProgressMessage,
  establishedAhpSessionPending,
  establishedAhpSessionError,
  taskStartupTaskId,
  taskStartup,
  taskStartupCloneProgress,
  savedHistoryEnabled,
  loadingPresentation,
  queueReadOnly,
  onContributedInstructions,
}: {
  ahpSession?: AhpSessionIdentifiers
  displaySession?: ChatDisplayIdentity
  savedHistory?: TaskAhpHistory
  savedHistoryError?: string
  stateOverride?: ChatState
  autoScrollRef?: Ref<TimelineAutoScrollHandle>
  afterContent?: ReactNode
  suppressProgressMessage?: boolean
  establishedAhpSessionPending?: boolean
  establishedAhpSessionError?: string
  taskStartupTaskId?: string
  taskStartup?: TaskStartup
  taskStartupCloneProgress?: number
  savedHistoryEnabled?: boolean
  loadingPresentation?: 'local' | 'page'
  queueReadOnly?: boolean
  onContributedInstructions?: (instructions: ContributedInstructionsMetadata, trigger: HTMLElement) => void
}) {
  const {state, errorMessage} = useNativeAhpTimelineFallbackState()
  const establishedAhpSession = useOptionalEstablishedAhpSession()
  const establishedView = useOptionalEstablishedAhpSessionViewState()
  const activeState = establishedAhpSession
    ? savedHistory
      ? establishedView.state
      : reconcilePersistedTimelineState(state, establishedView.state)
    : taskStartupTaskId
      ? undefined
      : (stateOverride ?? state)
  const activeErrorMessage = establishedAhpSession
    ? establishedView.error
    : (establishedAhpSessionError ?? errorMessage)

  return (
    <NativeAhpTimeline
      ahpSession={ahpSession}
      displaySession={displaySession}
      savedHistory={savedHistory}
      savedHistoryError={savedHistoryError}
      state={activeState}
      errorMessage={activeErrorMessage}
      autoScrollRef={autoScrollRef}
      afterContent={afterContent}
      suppressProgressMessage={suppressProgressMessage}
      establishedAhpSession={establishedAhpSession}
      establishedAhpSessionView={establishedAhpSession ? establishedView.snapshot : undefined}
      establishedAhpSessionPending={establishedAhpSessionPending}
      establishedAhpSessionError={establishedAhpSessionError}
      taskStartup={taskStartup}
      taskStartupCloneProgress={taskStartupCloneProgress}
      savedHistoryEnabled={savedHistoryEnabled}
      loadingPresentation={loadingPresentation}
      queueReadOnly={queueReadOnly}
      {...(onContributedInstructions ? {onContributedInstructions} : {})}
    />
  )
}
